import { ElementRef, inject, Injectable } from '@angular/core';
import { GtmService } from '../../../_shared/services/gtm.service';
import { TrackingEvents } from '../../../_shared/data/tracking-events.data';
import { SnowplowService } from '../../../_shared/services/snowplow.service';
import { Filters, IFilter, IFilterValue } from '../../../_shared/models/filters.model';
import { ICard } from '../../../_shared/models/card/card.model';
import { LinkService } from '../../../_shared/services/link.service';
import { debounceTime, Subject } from 'rxjs';
import { ViewService } from '../../../_shared/services/view.service';
import { CardCatalogueDataSource } from '../../../_shared/types/card-grid-data-source.type';
import { IFiltersActivated } from '../../../_shared/models/filters-activated.model';

@Injectable()
export class CardCatalogueTrackingService {
    private _gtmService = inject(GtmService);
    private _snowplowService = inject(SnowplowService);
    private _linkService = inject(LinkService);
    private _viewService = inject(ViewService);
    private _listInView = false;

    trackScroll(
        elementRef: ElementRef,
        scrolling: Subject<void>,
        dataSource: CardCatalogueDataSource
    ) {
        scrolling.pipe(debounceTime(100)).subscribe(() => {
            const el = elementRef.nativeElement;
            const inView = this._viewService.isTopInView(el);

            if (this._listInView || !inView) {
                return;
            }

            this._listInView = true;
            const cfg = { item_list_name: dataSource };
            this._gtmService.track(TrackingEvents.gtm['listView']([cfg]));
        });
    }

    trackSelect(selecting: Subject<[ICard, number]>, dataSource: CardCatalogueDataSource) {
        selecting.subscribe((item: [ICard, number]) => {
            const slug = this._linkService.getSlug(item[0].link.href);
            const cfg = {
                item_list_name: dataSource,
                index: item[1],
                content_name: slug,
            };

            this._gtmService.track(TrackingEvents.gtm['listItemSelect']([cfg]));
        });
    }

    trackFilter(filtering: Subject<Filters>, dataSource: CardCatalogueDataSource) {
        filtering.subscribe((x: Filters) => {
            const reg = /\s+/g;
            const filters = x.items.flatMap((x: IFilter) =>
                x.values
                    .filter((y: IFilterValue) => y.active)
                    .map((y: IFilterValue) => ({
                        id1: x.id.toLowerCase().replace(reg, '_'),
                        id2: y.id.toLowerCase().replace(reg, '_'),
                        value1: x.name.toLowerCase().replace(reg, '_'),
                        value2: y.name.toLowerCase().replace(reg, '_'),
                    }))
            );

            if (filters.length === 0) {
                return;
            }

            const id = filters.map((x: { id1: string; id2: string }) => {
                return `${x.id1}_${x.id2}`;
            });

            const val = filters.map((x: { value1: string; value2: string }) => {
                return `${x.value1}_${x.value2}`;
            });

            const cfg: { [key: string]: string } = {
                item_list_name: dataSource,
                filter_id: id.join(' '),
                filter_value: val.join(' '),
            };

            this._gtmService.track(TrackingEvents.gtm['listFilter']([cfg]));
        });
    }

    trackActivate(activating: Subject<IFiltersActivated>) {
        activating.subscribe((x: { category: string; values: IFilterValue[] }) => {
            const cfg2 = {
                filter_category: x.category,
                filter_values: x.values.map((x: IFilterValue) => x.name),
            };

            this._snowplowService.track('listFilter', cfg2);
        });
    }
}
